<template>
  <base-currency-offer
    :offer-data="filterData"
    :currency-settings="currencySettings"
    :currency-field-type="currencyFieldType"
    :should-set-default-values="shouldSetDefaultValues"
    :should-init-default-filters="false">
    <template #default="{ filtersList }">
      <div
        class="accounts-filters-row"
        :class="{
          'accounts-filters-row--extended':
            getBarePriorityFiltersCount(filtersList).count >= maxViewCount
            && getBarePriorityFiltersCount(filtersList).actualCount > maxViewCount,
        }">
        <div class="accounts-filters-row__container accounts-filters-row__container-main">
          <div
            class="accounts-filters-row__search-wrapper"
            :class="{
              'accounts-filters-row__search-wrapper--extended': getBarePriorityFiltersCount(filtersList).count === 2}
            ">
            <ui-search-input
              :value="searchValue"
              class="accounts-filters-row__search"
              size="small"
              placeholder="Search"
              @input="handleSearchInput" />
          </div>

          <div
            v-if="currencySettings.is_price_range_enabled"
            class="accounts-filters-row__price-range-wrapper">
            <dropdown-range
              v-bind="priceRangeProps"
              @input="handlePriceRange" />
          </div>

          <accounts-field
            v-for="field in filtersList.slice(0, getBarePriorityFiltersCount(filtersList).index)"
            :key="field.fieldData && field.fieldData.id ? field.fieldData.id : field.field._key"
            :field-instance="field"
            :is-disabled="field.fieldData._disabled"
            @focus="$emit('focus')"
            @blur="$emit('blur')"
            @input="$emit('input', $event)" />
        </div>

        <div
          v-if="getBarePriorityFiltersCount(filtersList).count >= maxViewCount
            && getBarePriorityFiltersCount(filtersList).actualCount > maxViewCount"
          class="button accounts-filters-row__show-more-button">
          <span class="accounts-filters-row__show-more-button-line" />

          <div
            class="accounts-filters-row__show-more-button-content"
            tabindex="0"
            @click="handleExpandButtonClick">
            More filters
            <img
              src="/images/icons/arrow-simple.svg"
              alt="Show more"
              width="16"
              height="16"
              class="accounts-filters-row__show-more-button-icon"
              :class="{'accounts-filters-row__show-more-button-icon--rotated': isExpanded}">
          </div>

          <span class="accounts-filters-row__show-more-button-line" />
        </div>

        <div
          ref="additionalFilters"
          class="accounts-filters-row__additional-filters"
          :class="{'accounts-filters-row__additional-filters--expanded': isExpanded}">
          <div :style="{ overflow: isOverflowHidden ? 'hidden' : 'unset' }">
            <div class="accounts-filters-row__container accounts-filters-row__container-additional">
              <accounts-field
                v-for="
                  field in filtersList.slice(getBarePriorityFiltersCount(filtersList).index, filtersList.length)
                "
                :key="field.fieldData && field.fieldData.id ? field.fieldData.id : field.field._key"
                :field-instance="field"
                :is-disabled="field.fieldData._disabled"
                @focus="$emit('focus')"
                @blur="$emit('blur')"
                @input="$emit('input', $event)" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </base-currency-offer>
</template>

<script>
import { _debounce as debounce } from 'Root/helpers/tools';
import AccountsField from 'Components/Accounts/AccountsField.vue';
import BaseCurrencyOffer from 'Components/Currency/BaseCurrencyOffer.vue';
import UiSearchInput from 'Components/ui/Input/UiSearchInput.vue';
import DropdownRange from 'Components/ui/Dropdown/DropdownRange.vue';
import { mapState } from 'vuex';

export default {
  name: 'AccountsFiltersRow',

  components: {
    DropdownRange, UiSearchInput, BaseCurrencyOffer, AccountsField,
  },

  props: {
    currencySettings: {
      type: Object,
      required: true,
    },

    currencyFieldType: {
      type: String,
      default: 'component_view',
      validate: (type) => ['component_view', 'component_edit'].includes(type),
    },

    shouldSetDefaultValues: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      filterData: {},
      isExpanded: false,
      isOverflowHidden: true,
    };
  },

  computed: {
    ...mapState('products', ['accountsPresets']),

    maxViewCount() {
      return this.currencySettings.is_price_range_enabled ? 3 : 4;
    },

    searchValue() {
      const activePreset = this.accountsPresets?.find((preset) => preset.id === +this.$route.query?.preset_id);

      if (activePreset?.params?.q) {
        return activePreset.params.q;
      }

      return this.$route.query?.q || '';
    },

    priceRangeProps() {
      const values = this.$route.query?.price_range?.split(',')?.map((el) => Number(el)) ?? [0, 2500];

      return {
        placeholder: 'Price',
        range: {
          values,
          borderValues: [0, 2500],
          step: 1,
          zones: [
            {
              from: 0,
              to: 2500,
              size: 100,
              step: 1,
            },
          ],
          scaleStepsCount: 1,
        },
      };
    },
  },

  mounted() {
    this.$refs.additionalFilters.addEventListener('transitionend', this.handleTransitionEnd);
  },

  beforeDestroy() {
    this.$refs.additionalFilters.removeEventListener('transitionend', this.handleTransitionEnd);
  },

  methods: {
    // eslint-disable-next-line func-names
    handleSearchInput: debounce(function (value) {
      this.$emit('search', value);
    }, 1000),

    // eslint-disable-next-line func-names
    handlePriceRange: debounce(function (value) {
      this.$emit('price-range', value);
    }, 1000),

    // SKY-6329: ими гордится скайкоуч...
    // Напридумывали всяких скрытых и двойных фильтров...
    // Удачи разобраться в этом))0
    getBarePriorityFiltersCount(filtersList) {
      const maxFiltersCount = this.maxViewCount;
      let actualFiltersCount = 0;
      let filtersCount = 0;
      let i = 0;

      filtersList.forEach((field) => {
        if (
          field.fieldData
          && field.fieldData.has_values_counter
          && !field.fieldData.is_hidden
          && filtersCount + 2 <= maxFiltersCount
        ) {
          actualFiltersCount += 2;
          filtersCount += 2;
          i += 1;
        } else if (
          field.fieldData
          && field.fieldData.has_values_counter
          && field.fieldData.is_hidden
          && filtersCount + 1 <= maxFiltersCount
        ) {
          actualFiltersCount += 1;
          filtersCount += 1;
          i += 1;
        } else if (
          !field.fieldData.is_hidden
          && filtersCount + 1 <= maxFiltersCount
        ) {
          actualFiltersCount += 1;
          filtersCount += 1;
          i += 1;
        } else if (
          field.fieldData.is_hidden
          && filtersCount + 1 <= maxFiltersCount
        ) {
          i += 1;
        } else if (!field.fieldData.is_hidden) {
          actualFiltersCount += 1;
        }
      });

      return {
        count: filtersCount,
        actualCount: actualFiltersCount,
        index: i,
      };
    },

    handleTransitionEnd() {
      this.isOverflowHidden = !this.isExpanded;
    },

    handleExpandButtonClick() {
      this.isOverflowHidden = true;
      this.$nextTick(() => {
        this.isExpanded = !this.isExpanded;
      });
    },
  },
};
</script>

<style scoped lang="scss">
$filter-height: 32px;
$filter-margin: 16px;
$show-more-button-height: 16px;
$container-padding: 24px;

.accounts-filters-row {
  position: relative;
  padding: 24px 24px;
  background: rgba(23, 30, 38, 0.80);
  border: 1px solid $color--basic-menu;

  border-radius: 16px;
  width: 100%;
  z-index: 2;
  transition: 0.3s;
}

.accounts-filters-row--extended {
  padding: 24px 24px 60px;
}

.accounts-filters-row__container {
  display: grid;
  grid-template-columns: repeat(6, 16.666%);
  grid-row-gap: 16px;
}

.accounts-filters-row__container-main {
  > *:nth-of-type(2) {
    /deep/ .accounts-field__item:first-of-type {
      grid-column: 3 span;
    }
  }
}

.accounts-filters-row__container-additional {
  margin-top: 16px;
}

.accounts-filters-row__additional-filters {
  display: grid;
  grid-template-rows: 0fr;
  transition: 0.3s;
}

.accounts-filters-row__additional-filters--expanded {
  grid-template-rows: 1fr;
}

.accounts-filters-row__search-wrapper,
.accounts-filters-row__price-range-wrapper {
  grid-column: 3 span;
  padding: 0 #{$filter-margin / 2};
}

.accounts-filters-row__search-wrapper--extended {
  grid-column: 3 span;
}

.accounts-filters-row__search {
  --background-color: #{rgba($color--basic-blue, 0.25)};
  --font-color: #{$color--basic-white};

  height: $filter-height;
  opacity: 1;
  padding: 0 12px !important;

  /deep/ .ui-search-input__input {
    line-height: 32px;
  }

  &.ui-search-input--active {
    --background-color: #{rgba($color--basic-blue, 0.35)};
  }
}

.accounts-filters-row__show-more-button {
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 16;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  padding: 0 24px 24px;
  border-radius: 15px;
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-white;
}

.accounts-filters-row__show-more-button-line {
  width: 100%;
  height: 1px;
  background-color: $color--basic-menu;
}

.accounts-filters-row__show-more-button-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.accounts-filters-row__show-more-button-icon {
  transform: rotate(90deg);
  transition: 0.3s;
}

.accounts-filters-row__show-more-button-icon--rotated {
  transform: rotate(-90deg);
}

@media screen and #{$screen-more-or-1024} {
  .accounts-filters-row__container {
    grid-template-columns: repeat(2, minmax(1px, 1fr));
  }

  .accounts-filters-row__container-main {
    > *:nth-of-type(2) {
      /deep/ .accounts-field__item:first-of-type {
        grid-column: 1 span;
      }
    }
  }

  .accounts-filters-row__search-wrapper {
    grid-column: 2 span;
  }

  .accounts-filters-row__price-range-wrapper {
    grid-column: 1 span;
  }
}

@media screen and #{$screen-more-or-1280} {
  .accounts-filters-row__container {
    grid-template-columns: repeat(6, 16.666%);
    grid-row-gap: 16px;
  }

  .accounts-filters-row__container-main {
    > *:nth-of-type(2) {
      /deep/ .accounts-field__item:first-of-type {
        grid-column: 3 span;
      }
    }
  }

  .accounts-filters-row__search-wrapper,
  .accounts-filters-row__price-range-wrapper {
    grid-column: 3 span;
  }
}
</style>
